import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type { LoginSocialBarSdkData } from '@wix/thunderbolt-components';
import { isExperimentOpen } from '@wix/editor-elements-common-utils';

import type {
  ILoginSocialBarMapperProps,
  LoginSocialBarDefinition,
  LoginSocialBarCSSVars,
  ILoginSocialBarStateValues,
} from '../LoginSocialBar.types';
import {
  ACCOUNT_BUTTON_ARIA_LABEL_TEXT,
  ARIA_LABELS_NAMESPACE,
  CUSTOM_LOGGED_IN_TEXT,
  Experiment,
  LOG_OUT_TEXT,
  LOGGED_IN_TEXT,
  LOGGED_OUT_TEXT,
  LOGIN_SOCIAL_BAR_NAMESPACE,
  OPTION_VALUE_TEXT,
  TRANSLATION_VALUE_DEFAULTS,
} from '../constants';
import { getCssMapperOutput } from './mappers';
import { getSanitizedCommonCompData, getTranslatedContent } from '../utils';

// TODO: typings are not accurate
export const props = withCompInfo<
  ILoginSocialBarMapperProps,
  LoginSocialBarDefinition,
  ILoginSocialBarMapperProps
>()(
  [
    'compData',
    'fullNameCompType',
    'language',
    'compId',
    'translate',
    'trackClicksAnalytics',
    'experiments',
  ],
  ({ compData, translate, experiments, ...compPropsRest }, carmiData) => {
    const enableLoggingInStateInSSR = isExperimentOpen(
      experiments,
      Experiment.EnableLoggingInStateInSSR,
    );

    const useNewCustomMenuPositioning = isExperimentOpen(
      experiments,
      Experiment.UseNewCustomMenuPositioning,
    );

    const useNewElementStructure = isExperimentOpen(
      experiments,
      Experiment.UseNewElementStructure,
    );

    const useNestedMenuItems = isExperimentOpen(
      experiments,
      Experiment.UseNestedMenuItems,
    );

    const {
      loggedInMember,
      loggedInAvatarIcon,
      loggedInCustomAvatar,
      loggedOutAvatarIcon,
      loggedOutCustomAvatar,
      loggedInTextState,
    } = getSanitizedCommonCompData(compData);

    const { loggedOutText, logOutText, loggedInText, customLoggedInText } =
      getTranslatedContent(
        {
          loggedOutText: {
            translationKey: LOGGED_OUT_TEXT,
            value: carmiData.loggedOutText,
          },
          logOutText: {
            translationKey: LOG_OUT_TEXT,
            value: carmiData.logOutText,
          },
          loggedInText: {
            translationKey: LOGGED_IN_TEXT,
            value: compData.loggedInText,
          },
          customLoggedInText: {
            translationKey: CUSTOM_LOGGED_IN_TEXT,
            value: compData.customLoggedInText,
          },
        },
        (key: string) => {
          return translate(LOGIN_SOCIAL_BAR_NAMESPACE, key);
        },
      );

    return {
      ...carmiData,
      ...compPropsRest,
      translations: {
        loggedOutText,
        logOutText,

        accountButtonAriaLabel:
          translate(ARIA_LABELS_NAMESPACE, ACCOUNT_BUTTON_ARIA_LABEL_TEXT) ||
          TRANSLATION_VALUE_DEFAULTS.accountButtonAriaLabel,

        menuOptionValueText:
          translate(LOGIN_SOCIAL_BAR_NAMESPACE, OPTION_VALUE_TEXT) ||
          TRANSLATION_VALUE_DEFAULTS.menuOptionValueText,
      },
      loggedInAvatarIcon,
      loggedInCustomAvatar,
      displayMode: loggedInMember,
      loggedOutAvatarIcon,
      loggedOutCustomAvatar,
      loggedInTextState,
      // TODO: move under translations object
      loggedInText,
      // TODO: move under translations object
      customLoggedInText,
      enableLoggingInStateInSSR,
      useNewCustomMenuPositioning,
      useNewElementStructure,
      useNestedMenuItems,
    };
  },
);

export const css = withCompInfo<
  LoginSocialBarCSSVars,
  LoginSocialBarDefinition,
  LoginSocialBarCSSVars
>()(
  ['compProps', 'compData', 'styleProperties', 'experiments'],
  ({ compProps, compData, styleProperties }, carmiCss) => {
    return getCssMapperOutput({
      compData,
      compProps,
      styleProperties,
      carmiCss,
    });
  },
);

export const sdkData = withCompInfo<
  LoginSocialBarSdkData,
  LoginSocialBarDefinition,
  LoginSocialBarSdkData
>()([], (_deps, carmiSdkData) => {
  return carmiSdkData;
});

export const requiredStateRefs: Array<keyof ILoginSocialBarStateValues> = [
  'memberDetails',
  'reportBi',
  'currentUrl',
  'logout',
  'promptLogin',
];

export const stateRefs =
  withStateRefsValues<keyof ILoginSocialBarStateValues>(requiredStateRefs);

export default createComponentMapperModel({ props, css, sdkData, stateRefs });
